import { toast } from '../../components/yoda/components/Toast/toast';
import i18n from '../../i18n';
import type { ExternalToast } from 'sonner';

/**
 * Notification utility for showing user-friendly error and info messages
 * Uses Sonner toast library under the hood
 */

// Toast configuration defaults
const DEFAULT_DURATION = 5000; // 5 seconds
const LONG_DURATION = 8000; // 8 seconds

export interface NotificationOptions extends ExternalToast {
  duration?: number;
  /** Optional key to dedupe/replace similar toasts */
  dedupeKey?: string;
}

type ToastKind = 'success' | 'error' | 'warning' | 'info' | 'loading';

/**
 * Emit a toast that will replace an existing one with the same dedupeKey/id
 */
export function showDedupedToast(
  kind: ToastKind,
  message: string,
  options?: NotificationOptions
): void {
  const { dedupeKey, id, ...rest } = options || {};
  const toastId = dedupeKey ?? id ?? message; // default to message text for light dedupe

  switch (kind) {
    case 'success':
      toast.success(message, { id: toastId, ...rest });
      break;
    case 'error':
      toast.error(message, { id: toastId, ...rest });
      break;
    case 'warning':
      toast.warning(message, { id: toastId, ...rest });
      break;
    case 'info':
      toast.info(message, { id: toastId, ...rest });
      break;
    case 'loading':
      toast.loading(message, { id: toastId, ...rest });
      break;
    default:
      toast(message, { id: toastId, ...rest });
  }
}

/**
 * Show session expired notification
 */
export function showSessionExpiredNotification(
  options?: NotificationOptions
): void {
  toast.error(i18n.t('errors.auth.sessionExpired'), {
    duration: LONG_DURATION,
    ...options,
  });
}

/**
 * Show network connection error notification
 */
export function showNetworkErrorNotification(
  message?: string,
  options?: NotificationOptions
): void {
  toast.error(message || i18n.t('errors.network.connectionFailed'), {
    duration: DEFAULT_DURATION,
    ...options,
  });
}

/**
 * Show CORS error notification (often indicates auth issues)
 */
export function showCorsErrorNotification(options?: NotificationOptions): void {
  toast.error(i18n.t('errors.network.corsError'), {
    duration: LONG_DURATION,
    description: i18n.t('errors.auth.sessionExpired'),
    ...options,
  });
}

/**
 * Show timeout error notification
 */
export function showTimeoutErrorNotification(
  options?: NotificationOptions
): void {
  toast.error(i18n.t('errors.network.timeout'), {
    duration: DEFAULT_DURATION,
    ...options,
  });
}

/**
 * Show authentication error notification
 */
export function showAuthenticationErrorNotification(
  message?: string,
  options?: NotificationOptions
): void {
  toast.error(message || i18n.t('errors.auth.failed'), {
    duration: LONG_DURATION,
    ...options,
  });
}

/**
 * Show server error notification
 */
export function showServerErrorNotification(
  message?: string,
  options?: NotificationOptions
): void {
  toast.error(message || i18n.t('errors.network.serverError'), {
    duration: DEFAULT_DURATION,
    ...options,
  });
}

/**
 * Show generic error notification
 */
export function showErrorNotification(
  message: string,
  options?: NotificationOptions
): void {
  toast.error(message, {
    duration: DEFAULT_DURATION,
    ...options,
  });
}

/**
 * Show success notification
 */
export function showSuccessNotification(
  message: string,
  options?: NotificationOptions
): void {
  toast.success(message, {
    duration: DEFAULT_DURATION,
    ...options,
  });
}

/**
 * Show info notification
 */
export function showInfoNotification(
  message: string,
  options?: NotificationOptions
): void {
  toast.info(message, {
    duration: DEFAULT_DURATION,
    ...options,
  });
}

/**
 * Show warning notification
 */
export function showWarningNotification(
  message: string,
  options?: NotificationOptions
): void {
  toast.warning(message, {
    duration: DEFAULT_DURATION,
    ...options,
  });
}

/**
 * Dismiss all active toasts
 */
export function dismissAllNotifications(): void {
  toast.dismiss();
}

/**
 * Dismiss a specific toast by ID
 */
export function dismissNotification(id: string | number): void {
  toast.dismiss(id);
}
